import React, { useEffect, useState } from 'react';

import { AggregatedAlertsSummary } from '@ariksa/notification/api';
import { Box, Flex, HStack, Stack } from '@chakra-ui/react';
import { each, isEmpty, map, orderBy, sum } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { selectDashboard } from 'containers/Dashboard/selectors';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { DashboardOverviewTooltipProps } from 'containers/Dashboard/utils/OverviewDashboardTooltip';
import { dashboardWidgetLineStyles } from 'containers/Dashboard/utils/styles';
import { renderEmptyRows } from 'containers/Dashboard/utils/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { toTitleCase } from 'utils/string';

interface Props {
  categoryClass?: string;
  tooltip?: DashboardOverviewTooltipProps;
  accounts: AggregatedAlertsSummary[];
}

export const TopFindings: React.FC<Props> = props => {
  const { categoryClass, accounts } = props;
  const { openFindings } = useSelector(selectDashboard);
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState<Record<string, any>[]>();
  const [categoryAccountsMapping, setCategoryAccountsMapping] = useState<
    Record<string, any>[]
  >();
  const { accountMapping } = useCloudAccountId();
  const [totalFindings, setTotalFindings] = useState(0);
  const [totalAccounts, setTotalAccounts] = useState(0);

  useEffect(() => {
    let items: Record<string, any> = {};
    if (!!categoryClass) {
      each(
        openFindings.data?.[categoryClass],
        c =>
          (items[c.category] = {
            CRITICAL: c.severity?.CRITICAL ?? 0,
            HIGH: c.severity?.HIGH ?? 0,
          }),
      );
    } else {
      each(openFindings.data, o => {
        each(
          o,
          c =>
            (items[c.category] = {
              CRITICAL:
                c.severity?.CRITICAL + (items[c.category]?.CRITICAL ?? 0),
              HIGH: c.severity?.HIGH + (items[c.category]?.HIGH ?? 0),
            }),
        );
      });
    }
    let filteredItems: Record<string, any> = {};
    each(items, (o, category) => {
      if (!!o?.CRITICAL)
        filteredItems[category] = { count: o?.CRITICAL, severity: 'critical' };
      else if (!!o?.HIGH)
        filteredItems[category] = { count: o?.HIGH, severity: 'high' };
    });

    setAllCategories(
      orderBy(
        map(filteredItems, (s, c) => ({ category: c, ...s })),
        ['severity', 'count'],
        ['asc', 'desc'],
      ),
    );
  }, [openFindings.data, categoryClass]);

  useEffect(() => {
    let mapping: Record<string, any>[] = [];
    each(accounts, o => {
      mapping[o.category] = o;
    });
    setCategoryAccountsMapping(mapping);
  }, [accounts, accountMapping]);

  useEffect(() => {
    setTotalFindings(sum(map(allCategories, o => o.count)));
  }, [allCategories]);

  useEffect(() => {
    let accountSet = new Set();
    each(allCategories, o => {
      const accounts = categoryAccountsMapping?.[o.category]?.account_ids;
      each(accounts, a => accountSet.add(a));
    });
    setTotalAccounts(accountSet.size);
  }, [allCategories, categoryAccountsMapping]);

  const renderLegend = (category, count, severity) => {
    const accounts =
      categoryAccountsMapping?.[category]?.account_ids?.length ?? 0;
    return (
      <HStack
        key={category}
        onClick={() => {
          navigate(
            '/findings/overview?tab=' +
              category +
              (!!categoryClass ? '&categoryClass=' + categoryClass : '') +
              '&severity=' +
              severity?.toUpperCase() +
              '&redirect=true',
          );
        }}
        cursor="pointer"
        spacing={6}
        {...dashboardWidgetLineStyles}
      >
        <Box h="full" w={1} bg={severity} borderRadius={2} />
        <Stack spacing={0} w="full" py={1}>
          <HStack
            justify="space-between"
            w="full"
            fontSize="dashboardRowHeader"
          >
            <Box>{toTitleCase(category)}</Box>
            <Box>{count}</Box>
          </HStack>
          <HStack
            justify="space-between"
            color={customTheme.colors.gray['250']}
            w="full"
            fontSize="dashboardRowSubHeader"
          >
            <Box>{toTitleCase(severity)} severity</Box>
            <Box>
              In {accounts} account{accounts === 1 ? '' : 's'}
            </Box>
          </HStack>
        </Stack>
      </HStack>
    );
  };

  return (
    <DashboardWidget
      label="Findings to Prioritize"
      noDataMessage={renderNoEntitiesFoundWithIcon('No findings')}
      noData={isEmpty(allCategories)}
      isLoading={openFindings.isLoading}
      subHeader={
        totalFindings +
        ' finding' +
        (totalFindings === 1 ? ', ' : 's, ') +
        totalAccounts +
        ' account' +
        (totalAccounts === 1 ? '' : 's')
      }
      content={
        <Flex w="full" h="full">
          {
            <Stack h="full" w="full" justify="space-between" spacing={0}>
              {map(allCategories, o =>
                renderLegend(o.category, o.count, o.severity),
              )}
              {renderEmptyRows(allCategories?.length)}
            </Stack>
          }
        </Flex>
      }
      styles={{ contentWrapper: { px: 0, pb: 0 } }}
    />
  );
};
